import { ElementRef, Injectable } from '@angular/core';
import { PosHttpService } from '../../services/pos.http.service';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import * as am5plugins_exporting from '@amcharts/amcharts5/plugins/exporting';
import { FnAppSessionService } from 'foundation';

@Injectable({
  providedIn: 'root'
})
export class LoginDataService {
  elementRef: any;
  chartData: { [key: string]: ChartDataPoint[] } = {};

  constructor(private posHttpSvcs: PosHttpService, private apSessionSvcs: FnAppSessionService){ }

  setChartData(data: any): void {
    this.chartData = data;
  }

  createCharts(elementRef: ElementRef): void {
    this.elementRef = elementRef;
    const chartContainer = this.elementRef.nativeElement.querySelector('#login_data');
    if (!chartContainer) {
      console.error('Chart container not found');
      return;
    }

    chartContainer.innerHTML = ''; 

    Object.entries(this.chartData).forEach(([schemaName, data]: [string, ChartDataPoint[]]) => {
      const chartDiv = document.createElement('div');
      chartDiv.style.width = '100%';
      chartDiv.style.height = '400px';
      chartContainer.appendChild(chartDiv);

      const root = am5.Root.new(chartDiv);
      if (root._logo) {
        root._logo.dispose();
      }
            
      root.container.set("background", am5.Rectangle.new(root, {
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x121418 : 0xf3f1f1),
        fillOpacity: 1
      }));
      
      root.setThemes([am5themes_Animated.new(root)]);

      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: true,
          panY: true,
          wheelX: 'panX',
          wheelY: 'zoomX',
          layout: root.verticalLayout,
          paddingLeft: 10,
          paddingRight: 20,
        })
      );

      chart.children.unshift(
        am5.Label.new(root, {
          text: `${schemaName} (LOGIN EVENT)`,
          fontSize: 18,
          fill: am5.color(0xFF3333),
          textAlign: 'center',
          x: am5.percent(50),
          centerX: am5.percent(50),
          fontWeight: 'bold',
        })
      );

      const xAxisRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });
      xAxisRenderer.grid.template.setAll({
        visible: false,
      });
      xAxisRenderer.labels.template.setAll({
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        rotation: -90,
      });
      xAxisRenderer.setAll({
        stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        strokeOpacity: 1,
        strokeWidth: 1,
      });
      const xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          renderer: xAxisRenderer,
          categoryField: 'time',
          tooltip: am5.Tooltip.new(root, {}),
        })
      );
      xAxis.data.setAll(data);
     
      xAxis.children.push(
        am5.Label.new(root, {
          text: 'Time',
          fontSize: 14,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
          rotation: 0,
          x: am5.percent(50),
          centerX: am5.percent(50),
          y: am5.percent(100),
          centerY: am5.percent(100),
          paddingTop: 5,
        })
      );

      const yAxisLeftRenderer = am5xy.AxisRendererY.new(root, { opposite: false });
      yAxisLeftRenderer.grid.template.setAll({
        visible: false,
      });
      yAxisLeftRenderer.setAll({
        stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        strokeOpacity: 1,
        strokeWidth: 1,
      });
      yAxisLeftRenderer.labels.template.setAll({
        fontSize: 12,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        visible: true,
        paddingRight: 10,
      });
      const yAxisLeft = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: yAxisLeftRenderer,
          tooltip: am5.Tooltip.new(root, {}),
          min: 0,
          extraMax: 0.1,
          numberFormat: '#',
        })
      );
      yAxisLeft.children.unshift(
        am5.Label.new(root, {
          text: 'Count',
          fontSize: 14,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
          rotation: -90,
          x: -2,
          y: am5.percent(50),
          centerY: am5.percent(50),
          centerX: am5.percent(50),
        })
      );

      const yAxisRightRenderer = am5xy.AxisRendererY.new(root, { opposite: true });
      yAxisRightRenderer.grid.template.setAll({
        visible: false,
      });
      yAxisRightRenderer.setAll({
        stroke: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFF3333 : 0x000000),
        strokeOpacity: 1,
        strokeWidth: 1,
      });
      yAxisRightRenderer.labels.template.setAll({
        fontSize: 12,
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
        visible: true,
        paddingLeft: 5,
      });
      const yAxisRight = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: yAxisRightRenderer,
          tooltip: am5.Tooltip.new(root, {}),
          min: 0,
          extraMax: 0.1,
          numberFormat: '#.##',
        })
      );
      yAxisRight.children.push(
        am5.Label.new(root, {
          text: 'Average Time',
          fontSize: 14,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x000000),
          rotation: -90,
          x: 40,
          y: am5.percent(50),
          centerY: am5.percent(50),
          centerX: am5.percent(50),
        })
      );

      const barSeries = chart.series.push(
        am5xy.ColumnSeries.new(root, {
          name: 'Count',
          xAxis: xAxis,
          yAxis: yAxisLeft,
          valueYField: 'count',
          categoryXField: 'time',
          tooltip: am5.Tooltip.new(root, { 
          labelText: 'Count: {valueY}\n{hostDetails}' 
          }),
        })
      );

      data.forEach((d: any) => {
        if (d.hostWiseCount) {
          d.hostDetails = Object.entries(d.hostWiseCount)
            .filter(([_, details]) => (details as { serverCount: number }).serverCount > 0) 
            .map(([host, details]) => `${host}: ${(details as { serverCount: number }).serverCount}`)
            .join('\n') || 'None';
        }
      });
      
      barSeries.data.setAll(data);
      am5plugins_exporting.Exporting.new(root, {
        menu: am5plugins_exporting.ExportingMenu.new(root, {}),
        dataSource: this.chartData
      });

      barSeries.columns.template.setAll({
        maxWidth: 50,
        width: am5.percent(80),
        fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xE7A84A : 0x87CEFA),
        strokeOpacity: 0,
      });

      barSeries.bullets.push(() =>
        am5.Bullet.new(root, {
          locationY: 1,
          sprite: am5.Label.new(root, {
            text: '{valueY}',
            fontSize: 12,
            fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0x59ABF6 : 0x000000),
            centerX: am5.percent(50),
            centerY: am5.percent(50),
            dy: -10,
            populateText: true,
          }),
        })
      );

      const lineSeries = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: 'Average Time',
          xAxis: xAxis,
          yAxis: yAxisRight,
          valueYField: 'avg',
          categoryXField: 'time',
          stroke: am5.color(0xFF0000),
          tooltip: am5.Tooltip.new(root, { labelText: 'Average Time: {valueY}' }),
        })
      );
      lineSeries.data.setAll(data);
      lineSeries.strokes.template.setAll({
        strokeWidth: 3,
        stroke: am5.color(0x000000)
      });

      lineSeries.bullets.push(() =>
        am5.Bullet.new(root, {
          locationY: 0.5,
          sprite: am5.Label.new(root, {
            text: '{valueY}',
            fontSize: 12,
            fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x274e13),
            centerX: am5.percent(50),
            centerY: am5.percent(-20),
            populateText: true,
          }),
        })
      );
      
      lineSeries.bullets.push(() => {
        const bullet = am5.Bullet.new(root, {
          sprite: am5.Circle.new(root, {
            radius: 5,
            fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xefb96a : 0xffffff),
            stroke: am5.color(0xFFFFFF),
            strokeWidth: 2,
          }),
        });
      
        const circle = bullet.get('sprite') as am5.Circle;
        if (circle) {
          circle.adapters.add('fill', (fill, target) => {
            const dataItem = target.dataItem as am5.DataItem<am5xy.ILineSeriesDataItem>;
            if (dataItem) {
              const dataContext = dataItem.dataContext as ChartDataPoint;
              return dataContext.avg >= 10 ? am5.color(0xFF0000) : am5.color(this.apSessionSvcs.isDarkTheme() ? 0x87CEFA : 0x000000); 
            }
            return fill;
          });
        }
      
        return bullet;
      });

      const cursor = chart.set('cursor', am5xy.XYCursor.new(root, {}));
      cursor.lineX.set('visible', false);
      cursor.lineY.set('visible', false);

      const countMax = Math.max(...data.map((d: any) => d.count));
      const countMin = Math.min(...data.map((d: any) => d.count));
      const avgMax = Math.max(...data.map((d: any) => d.avg));
      const avgMin = Math.min(...data.map((d: any) => d.avg));
      chart.children.push(
        am5.Label.new(root, {
          text: `[#87CEFA]■[/] Count: Max: ${countMax},  Min: ${countMin}    [#FF0000]—[/] Average Time: Max: ${avgMax.toFixed(2)},  Min: ${avgMin.toFixed(2)}`,
          fontSize: 14,
          fill: am5.color(this.apSessionSvcs.isDarkTheme() ? 0xFFFFFF : 0x333333),
          fontWeight: 'normal',
          textAlign: 'left',
          x: am5.percent(0),
          centerX: am5.percent(0),
          paddingLeft: 10,
          paddingTop: 10,
          paddingBottom: 10,
        })
      );
    });
  }
}

interface ChartDataPoint {
  schema?: string;
  avg: number;
  busiDate?: string;
  count: number;
  index?: number;
  time: string;
  id?: string;
  hostWiseCount?: string;
  serverMapList?: any[];
  color?: string; 
}
